import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, TrendingUp, DollarSign, Network, ArrowRight, Gift, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { mlmStructure } from '../data/mock';

const Referral = () => {
  const navigate = useNavigate();

  return (
    <div className="dark-container min-h-screen pt-28 pb-20">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Hero */}
        <div className="text-center mb-20">
          <div className="inline-block px-4 py-2 bg-brand-primary/10 border border-brand-primary rounded-sm mb-6">
            <span className="text-brand-primary text-sm font-medium">Earn Passive Income</span>
          </div>
          <h1 className="display-large text-white mb-6">Build Your Network, Earn Forever</h1>
          <p className="body-large text-white/70 max-w-3xl mx-auto leading-relaxed">
            Our revolutionary multi-level referral system rewards you for building a community. 
            Earn commissions up to 5 levels deep on every investment your network makes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Button className="btn-primary" onClick={() => navigate('/signup')}>
              Start Building Now
              <ArrowRight size={18} />
            </Button>
            <Button className="btn-secondary" onClick={() => navigate('/dashboard')}>
              View Dashboard
            </Button>
          </div>
        </div>

        {/* Benefits */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          <Card className="bg-white/5 border border-white/25 backdrop-blur-xl hover:border-brand-primary transition-all duration-300">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center">
                <DollarSign className="text-brand-primary" size={36} />
              </div>
              <h3 className="heading-2 text-white mb-3">Passive Income</h3>
              <p className="text-white/60 leading-relaxed">
                Earn commissions automatically on every investment made by your network. 
                Build once, earn forever.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white/5 border border-white/25 backdrop-blur-xl hover:border-brand-primary transition-all duration-300">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center">
                <Network className="text-brand-primary" size={36} />
              </div>
              <h3 className="heading-2 text-white mb-3">Multi-Level System</h3>
              <p className="text-white/60 leading-relaxed">
                Earn from 5 levels of referrals. Your network's success becomes your success with our deep commission structure.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white/5 border border-white/25 backdrop-blur-xl hover:border-brand-primary transition-all duration-300">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center">
                <TrendingUp className="text-brand-primary" size={36} />
              </div>
              <h3 className="heading-2 text-white mb-3">Unlimited Growth</h3>
              <p className="text-white/60 leading-relaxed">
                No cap on earnings. The more you grow your network, the more you earn. Sky's the limit!
              </p>
            </CardContent>
          </Card>
        </div>

        {/* How It Works */}
        <div className="bg-white/5 border border-white/25 rounded-sm p-12 backdrop-blur-xl mb-20">
          <div className="text-center mb-12">
            <h2 className="display-medium text-white mb-4">How It Works</h2>
            <p className="body-medium text-white/70">Simple steps to start earning</p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-brand-primary text-black rounded-sm flex items-center justify-center text-2xl font-bold">
                1
              </div>
              <h3 className="text-white font-semibold mb-2">Sign Up</h3>
              <p className="text-white/60 text-sm">Create your account and get your unique referral code</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-brand-primary text-black rounded-sm flex items-center justify-center text-2xl font-bold">
                2
              </div>
              <h3 className="text-white font-semibold mb-2">Share Link</h3>
              <p className="text-white/60 text-sm">Share your referral link with friends, family, and your network</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-brand-primary text-black rounded-sm flex items-center justify-center text-2xl font-bold">
                3
              </div>
              <h3 className="text-white font-semibold mb-2">They Invest</h3>
              <p className="text-white/60 text-sm">Your referrals invest in token launches on the platform</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-brand-primary text-black rounded-sm flex items-center justify-center text-2xl font-bold">
                4
              </div>
              <h3 className="text-white font-semibold mb-2">You Earn</h3>
              <p className="text-white/60 text-sm">Receive instant commissions from your entire network</p>
            </div>
          </div>
        </div>

        {/* Bonus Programs */}
        <div className="grid md:grid-cols-2 gap-8 mb-20">
          <Card className="bg-white/5 border border-brand-primary backdrop-blur-xl">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 flex-shrink-0 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center">
                  <Gift className="text-brand-primary" size={28} />
                </div>
                <div>
                  <h3 className="heading-2 text-white mb-3">Welcome Bonus</h3>
                  <p className="text-white/70 mb-4 leading-relaxed">
                    New members who invest within 7 days receive an extra 5% bonus on their first investment, 
                    plus double commission on their first 3 referrals.
                  </p>
                  <div className="text-brand-primary font-semibold">+5% Bonus + Double Commission</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/5 border border-brand-primary backdrop-blur-xl">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 flex-shrink-0 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center">
                  <Users className="text-brand-primary" size={28} />
                </div>
                <div>
                  <h3 className="heading-2 text-white mb-3">Leadership Bonus</h3>
                  <p className="text-white/70 mb-4 leading-relaxed">
                    Build a network of 100+ active investors and unlock monthly leadership bonuses 
                    up to $10,000 based on your team's performance.
                  </p>
                  <div className="text-brand-primary font-semibold">Up to $10,000/month</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* CTA */}
        <div className="bg-white/5 border border-brand-primary rounded-sm p-12 backdrop-blur-xl text-center">
          <h2 className="display-medium text-white mb-4">Ready to Build Your Empire?</h2>
          <p className="body-large text-white/70 mb-8 max-w-2xl mx-auto">
            Start earning passive income today. Join thousands of network builders who are creating 
            financial freedom through MatrixLaunch.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="btn-primary" onClick={() => navigate('/signup')}>
              Get Your Referral Code
              <ArrowRight size={18} />
            </Button>
            <Button className="btn-secondary" onClick={() => navigate('/contact')}>
              Contact Support
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Referral;
